
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Building2,
  Factory,
  GraduationCap,
  Heart,
  Landmark,
  ShoppingCart,
  Target,
} from 'lucide-react';

// --- Button Component ---
function Button({ children, onClick, className, variant, ...props }) {
  const baseClasses = 'inline-flex items-center justify-center rounded-lg font-medium transition-all';
  const variantClasses = variant === 'outline' ? 'border-2' : '';

  return (
    <button className={`${baseClasses} ${variantClasses} ${className}`} onClick={onClick} {...props}>
      {children}
    </button>
  );
}

// --- Card Components ---
function Card({ children, className, onClick }) {
  return <div className={`rounded-xl border bg-card text-card-foreground shadow ${className}`} onClick={onClick}>{children}</div>;
}

function CardContent({ children, className }) {
  return <div className={`p-6 ${className || ''}`}>{children}</div>;
}


const industries = [
  {
    id: 'financial',
    name: 'Financial Services',
    icon: Landmark,
    color: 'from-blue-500 to-blue-600',
    challenges: [
      'Complex regulatory requirements (SOX, PCI DSS)',
      'Sensitive financial data protection',
      'Real-time fraud detection needs',
      'Audit trail requirements',
    ],
    solutions: [
      'Automated SOX compliance workflows',
      'PCI DSS evidence collection',
      'Real-time risk monitoring',
      'Comprehensive audit trails',
      'Identity access governance',
    ],
    capabilities: { compliance: 'SOX, PCI DSS, GLBA', focus: 'Financial Data Protection' },
  },
  {
    id: 'healthcare',
    name: 'Healthcare',
    icon: Heart,
    color: 'from-red-500 to-red-600',
    challenges: [
      'HIPAA compliance complexity',
      'Patient data security',
      'Medical device management',
      'Third-party risk assessment',
    ],
    solutions: [
      'HIPAA-ready compliance framework',
      'PHI access controls and monitoring',
      'Medical device inventory tracking',
      'Vendor risk assessments',
      'Breach notification automation',
    ],
    capabilities: { compliance: 'HIPAA, HITRUST, SOC 2', focus: 'PHI Protection' },
  },
  {
    id: 'education',
    name: 'Education',
    icon: GraduationCap,
    color: 'from-green-500 to-green-600',
    challenges: [
      'FERPA student record protection',
      'Limited cybersecurity budgets',
      'Diverse user populations',
      'Legacy system integration',
    ],
    solutions: [
      'FERPA compliance automation',
      'Cost-effective security controls',
      'Student/faculty identity management',
      'Legacy system risk assessment',
      'Incident response planning',
    ],
    capabilities: { compliance: 'FERPA, GLBA, State Laws', focus: 'Student Data Protection' },
  },
  {
    id: 'government',
    name: 'Government',
    icon: Building2,
    color: 'from-purple-500 to-purple-600',
    challenges: [
      'FedRAMP and FISMA requirements',
      'Citizen data protection',
      'Inter-agency coordination',
      'Budget constraints',
    ],
    solutions: [
      'FedRAMP-ready security controls',
      'Citizen privacy protection',
      'Multi-agency governance',
      'Cost-optimized deployments',
      'Continuous monitoring',
    ],
    capabilities: { compliance: 'FedRAMP, FISMA, NIST', focus: 'Citizen Data Protection' },
  },
  {
    id: 'manufacturing',
    name: 'Manufacturing',
    icon: Factory,
    color: 'from-orange-500 to-orange-600',
    challenges: [
      'OT/IT security convergence',
      'Supply chain risk',
      'Industrial control systems',
      'Regulatory compliance',
    ],
    solutions: [
      'OT/IT unified monitoring',
      'Supply chain risk assessment',
      'ICS security controls',
      'Industry-specific compliance',
      'Asset discovery and tracking',
    ],
    capabilities: { compliance: 'IEC 62443, NIST CSF', focus: 'OT/IT Security' },
  },
  {
    id: 'retail',
    name: 'Retail',
    icon: ShoppingCart,
    color: 'from-pink-500 to-pink-600',
    challenges: [
      'PCI DSS compliance',
      'Customer data protection',
      'Multi-location security',
      'Seasonal workforce management',
    ],
    solutions: [
      'PCI DSS automation',
      'Customer privacy controls',
      'Multi-site governance',
      'Temporary access management',
      'Point-of-sale security',
    ],
    capabilities: { compliance: 'PCI DSS v4.0, CCPA', focus: 'Payment Data Protection' },
  },
];

export function IndustriesSection({ onNavigateToTop }) {

  // Navigate to individual industry page when tab is clicked
  const handleIndustryClick = (industryId) => {
    onNavigateToTop(`/industries/${industryId}`);
  };

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          viewport={{ once: true }}
          className="text-center mb-16"
          transition={{ duration: 0.8 }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <div className="inline-flex items-center bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Target className="w-4 h-4 mr-2" />
            Industry-Specific Solutions
          </div>

          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            Tailored for Your Industry
          </h2>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            BOSSS understands that every industry has unique compliance requirements and security challenges.
            Our platform adapts to your specific needs with pre-configured frameworks and industry expertise.
          </p>
        </motion.div>

        {/* Industry Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {industries.map((industry, index) => {
            const IndustryIcon = industry.icon;
            return (
              <motion.div
                key={industry.id}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card
                  className="h-full cursor-pointer hover:shadow-xl transition-all group border-gray-200 hover:border-blue-300"
                  onClick={() => handleIndustryClick(industry.id)}
                >
                  <CardContent className="p-6">
                    <div className={`w-14 h-14 bg-gradient-to-br ${industry.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <IndustryIcon className="w-7 h-7 text-white" />
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-2">{industry.name}</h3>

                    {/* Key Capabilities */}
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">Frameworks</span>
                        <span className="font-medium text-blue-600 text-xs">{industry.capabilities.compliance}</span>
                      </div>

                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">Focus</span>
                        <span className="font-medium text-green-600 text-xs">{industry.capabilities.focus}</span>
                      </div>
                    </div>

                    {/* Top Challenges Preview */}
                    <div className="space-y-1 mb-4">
                      {industry.challenges.slice(0, 2).map((challenge, idx) => (
                        <div key={idx} className="flex items-start text-sm">
                          <div className="w-1.5 h-1.5 bg-red-400 rounded-full mt-1.5 mr-2 flex-shrink-0" />
                          <span className="text-gray-600 line-clamp-1">{challenge}</span>
                        </div>
                      ))}
                    </div>

                    <div className="flex items-center text-blue-600 font-medium text-sm group-hover:gap-2 transition-all">
                      <span>Learn More</span>
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="text-center"
          viewport={{ once: true }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-12 border border-blue-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              See How BOSSS Works for Your Industry
            </h3>

            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Schedule a personalized demo to see how BOSSS can address your industry-specific
              compliance and security challenges with tailored solutions.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg transition-all hover:scale-105"
                onClick={() => onNavigateToTop('/demo')}
              >
                Request Industry Demo
              </Button>

              <Button
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-3 rounded-lg transition-all hover:scale-105"
                onClick={() => onNavigateToTop('/contact')}
              >
                Talk to an Expert
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
